import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider, QueryErrorResetBoundary } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { queryClient } from "@/lib/queryClient";
import { Suspense, type ComponentType, type ReactNode } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { shouldResolveAssistantHostname } from "@/lib/assistantDomain";
import { ModalsProvider } from "@/contexts/ModalsContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { WorkspaceMfaGate } from "@/components/security/WorkspaceMfaGate";
import { CsvImportProgressProvider } from "@/contexts/CsvImportProgressContext";
import { CsvImportProgressPill } from "@/components/csv-progress/CsvImportProgressPill";
import { ViewLoadingFallback } from "@/components/ui/ViewLoadingFallback";
import { useAuthRouteGate } from "@/hooks/useAuthRouteGate";
import LegacyIntegrationsRedirect from "@/pages/LegacyIntegrationsRedirect";
import LegacySettingsRedirect from "@/pages/LegacySettingsRedirect";
import { RoutePerformanceAnalytics } from "@/components/RoutePerformanceAnalytics";
import { RouteAnalyticsTracker } from "@/components/RouteAnalyticsTracker";

const LazyAuthProvider = lazyWithRetry(
  () => import('@/contexts/AuthContext').then(module => ({ default: module.AuthProvider })),
  'AuthProvider'
);
const LazyUserSettingsProvider = lazyWithRetry(
  () => import('@/contexts/UserSettingsContext').then(module => ({ default: module.UserSettingsProvider })),
  'UserSettingsProvider'
);

const LazyAuthenticatedAppChrome = lazyWithRetry(
  () => import('@/components/app/AuthenticatedAppChrome').then(m => ({ default: m.AuthenticatedAppChrome })),
  'AuthenticatedAppChrome'
);
// EntityDetailOverlay moved to Index.tsx (inside WorkspaceProvider)
// Lazy-loaded route components for better code splitting
import {
  LazyIndex,
  LazyCalculatorDraftEditor,
  LazyPublicCalculator,
  LazyTrackingGoRedirect,
  LazyAuth,
  LazyAdmin,
  LazyAdminLogin,
  LazyAdminPrompts,
  LazyAIQualityDashboard,
  LazyAcceptInvitation,
  LazyAcceptPartnerInvitation,
  LazyTestimonialResponse,
  LazyVideoPlayer,
  LazyBugReportFeedback,
  LazyPaymentSuccess,
  LazyWiki,
  LazyWikiArticle,
  LazyPossibleDuplicates,
  LazyValueAuditReview,
  LazyZapierAuthorize,
  LazyOAuthConsent,
  LazyConnectedApps,
  LazyEmailOAuthCallback,
  LazyIntegrationOAuthCallback,
  LazyOnboardChat,
  LazyAIDemo,
  LazyDemoPreview,
  LazyDemoShareStatus,
  LazySharedConversation,
  LazyPublicDealRoom,
  LazyPublicDemoWalkthrough,
  LazyDealReview,
  LazyMeetingPrep,
  LazyPipelineReview,
  LazyProposalTeardown,
  LazyAccountExpansion,
  LazyWinLoss,
  LazyClientHealth,
  LazyCompetitive,
  LazyBusinessPlanning,
  LazyQbrPrep,
  LazyReferralNetwork,
  LazyAbmStrategist,
  LazyActivityHabits,

  LazyAuthorityReports,
  LazyPublicAuthorityReport,
  LazyPublicCompanyBrief,
  LazyAuthorityScan,
  LazyNotFound } from "@/lib/lazy-routes";


// queryClient moved to src/lib/queryClient.ts

/** Redirect /ai-demo/shares/:shareCode → /assistant/shares/:shareCode */
function AIDemoShareRedirect() {
  const { shareCode } = useParams();
  return <Navigate to={`/assistant/shares/${shareCode}`} replace />;
}

/** Legacy /brief/:shareCode → canonical /audit/:shareCode (preserve query). */
function CompanyBriefLegacyRedirect() {
  const { shareCode } = useParams();
  const location = useLocation();
  return <Navigate to={`/audit/${shareCode}${location.search}`} replace />;
}

// /content-ops moved into Workspace > Settings (Content Publishing section,
// Submagic + ContentStudio key entry). The old standalone page was removed
// once Workspace covered every surface.
function ContentOpsRedirect() {
  return (
    <Navigate
      to="/"
      replace
      state={{ activeView: 'CRM', crmSubView: 'workspace' }}
    />
  );
}

function ExternalRedirect({ to }: { to: string }) {
  window.location.replace(to);
  return null;
}

function AuthenticatedWorkspaceRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<ViewLoadingFallback />}>
      <LazyAuthenticatedAppChrome>
        {children}
      </LazyAuthenticatedAppChrome>
    </Suspense>
  );
}

function ProtectedAppRoute() {
  const { fallback } = useAuthRouteGate();
  if (fallback) return fallback;

  return (
    <AuthenticatedWorkspaceRoute>
      <LazyIndex />
    </AuthenticatedWorkspaceRoute>
  );
}

function RootRoute() {
  if (typeof window !== 'undefined' && shouldResolveAssistantHostname(window.location.hostname)) {
    return <LazyAIDemo />;
  }

  return <ProtectedAppRoute />;
}

// Coaching routes follow the same shape:
//   /coaching/<key>/:sessionId → <ProtectedCoachingRoute><Lazy*/></ProtectedCoachingRoute>
// Listed here as data so adding a new coaching surface is one line.
const COACHING_ROUTES: Array<{ path: string; Component: ComponentType }> = [
  { path: 'meeting_prep', Component: LazyMeetingPrep },
  { path: 'pipeline_review', Component: LazyPipelineReview },
  { path: 'proposal_teardown', Component: LazyProposalTeardown },
  { path: 'account_expansion', Component: LazyAccountExpansion },
  { path: 'win_loss', Component: LazyWinLoss },
  { path: 'client_health', Component: LazyClientHealth },
  { path: 'competitive', Component: LazyCompetitive },
  { path: 'business_planning', Component: LazyBusinessPlanning },
  { path: 'qbr_prep', Component: LazyQbrPrep },
  { path: 'referral_network', Component: LazyReferralNetwork },
  { path: 'abm_strategist', Component: LazyAbmStrategist },
  { path: 'activity_habits', Component: LazyActivityHabits },
];

function ProtectedWorkspaceRoute({ children }: { children: ReactNode }) {
  const { fallback, user } = useAuthRouteGate();
  if (fallback) return fallback;

  return (
    <WorkspaceProvider user={user}>
      <WorkspaceMfaGate>
        <Suspense fallback={<ViewLoadingFallback />}>{children}</Suspense>
      </WorkspaceMfaGate>
    </WorkspaceProvider>
  );
}

function ProtectedCoachingRoute({ children }: { children: ReactNode }) {
  return <ProtectedWorkspaceRoute>{children}</ProtectedWorkspaceRoute>;
}

function AppAuthBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  if (location.pathname.startsWith('/tool/')) return <LazyPublicCalculator />;
  return <Suspense fallback={<ViewLoadingFallback />}><LazyAuthProvider><LazyUserSettingsProvider><CsvImportProgressProvider>{children}<CsvImportProgressPill /></CsvImportProgressProvider></LazyUserSettingsProvider></LazyAuthProvider></Suspense>;
}

function AppRouteAnalytics() {
  const location = useLocation();
  return location.pathname.startsWith('/tool/') || location.pathname.startsWith('/share/asset/') ? null : <RouteAnalyticsTracker />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary onReset={reset}>
            <TooltipProvider>
              <ModalsProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                    <RoutePerformanceAnalytics />
                    <AppRouteAnalytics />
                    <AppAuthBoundary>
                        <Routes>
                          <Route path="/" element={<RootRoute />} />
                          <Route path="/tools/calculators/:id/edit" element={<ProtectedWorkspaceRoute><LazyCalculatorDraftEditor /></ProtectedWorkspaceRoute>} />
                          <Route path="/contacts/:id" element={<ProtectedAppRoute />} />
                          <Route path="/companies/:id" element={<ProtectedAppRoute />} />
                          <Route
                            path="/companies/:id/audit-review"
                            element={
                              <ProtectedWorkspaceRoute>
                                <LazyValueAuditReview />
                              </ProtectedWorkspaceRoute>
                            }
                          />
                          <Route path="/deals/:id" element={<ProtectedAppRoute />} />
                          <Route path="/campaigns/:id" element={<ProtectedAppRoute />} />
                          <Route path="/revenue/:id" element={<ProtectedAppRoute />} />
                          <Route path="/auth" element={<LazyAuth />} />
                          <Route path="/login" element={<LazyAuth />} />
                          <Route path="/social" element={<Navigate to="/?view=Social" replace />} />
                          <Route path="/admin/login" element={<LazyAdminLogin />} />
                          <Route path="/admin" element={<LazyAdmin />} />
                          <Route path="/admin/prompts" element={<LazyAdminPrompts />} />
                          <Route path="/admin/ai-quality" element={<LazyAIQualityDashboard />} />
                          <Route path="/admin/analytics" element={<Navigate to="/admin/ai-quality" replace />} />
                          <Route path="/accept-invitation/:token" element={<LazyAcceptInvitation />} />
                          <Route path="/accept-partner-invitation/:token" element={<LazyAcceptPartnerInvitation />} />
                          <Route path="/testimonial-response/:token" element={<LazyTestimonialResponse />} />
                          <Route path="/v/:videoId" element={<LazyVideoPlayer />} />
                          <Route path="/bug-report-feedback/:token" element={<LazyBugReportFeedback />} />
                          <Route path="/payment-success" element={<LazyPaymentSuccess />} />
                          <Route path="/wiki" element={<LazyWiki />} />
                          <Route path="/wiki/:slug" element={<LazyWikiArticle />} />
                          <Route path="/intelligence" element={<Navigate to="/?view=DataQuality" replace />} />
                          <Route path="/data-quality" element={<Navigate to="/?view=DataQuality" replace />} />
                          <Route
                            path="/possible-duplicates"
                            element={
                              <ProtectedWorkspaceRoute>
                                <LazyPossibleDuplicates />
                              </ProtectedWorkspaceRoute>
                            }
                          />
                          <Route path="/content-ops" element={<ContentOpsRedirect />} />
                          <Route path="/settings" element={<LegacySettingsRedirect />} />
                          <Route path="/integrations" element={<LegacyIntegrationsRedirect />} />
                          <Route path="/settings/integrations" element={<LegacyIntegrationsRedirect />} />
                          <Route path="/zapier/authorize" element={<LazyZapierAuthorize />} />
                          <Route path="/oauth/consent" element={<LazyOAuthConsent />} />
                          <Route path="/settings/connected-apps" element={<LazyConnectedApps />} />
                          <Route path="/oauth/email-callback" element={<LazyEmailOAuthCallback />} />
                          <Route path="/oauth/integration-callback" element={<LazyIntegrationOAuthCallback />} />
                          <Route path="/onboard" element={<LazyOnboardChat />} />
                          <Route path="/assistant" element={<LazyAIDemo />} />
                          <Route path="/ai-demo" element={<Navigate to="/assistant" replace />} />
                          <Route path="/demo-preview" element={<LazyDemoPreview />} />
                          <Route path="/assistant/shares/:shareCode" element={<LazyDemoShareStatus />} />
                          {/* Per-workspace slug (must come AFTER /assistant/shares/:shareCode so that route still wins). */}
                          <Route path="/assistant/:slug" element={<LazyAIDemo />} />
                          <Route path="/ai-demo/shares/:shareCode" element={<AIDemoShareRedirect />} />
                          <Route path="/s/:shareCode" element={<LazySharedConversation />} />
                          <Route path="/room/:workspace/:company/:shortId" element={<LazyPublicDealRoom />} />
                          <Route path="/demo/:slug" element={<LazyPublicDemoWalkthrough />} />
                          <Route path="/deal-review/:sessionId" element={<LazyDealReview />} />
                          <Route path="/coaching/*" element={<ProtectedAppRoute />} />
                          {COACHING_ROUTES.map(({ path }) => (
                            <Route key={`legacy-${path}`} path={`/coaching/${path}`} element={<Navigate to="/?view=CRM&subview=coaching" replace />} />
                          ))}
                          {COACHING_ROUTES.map(({ path, Component }) => (
                            <Route
                              key={path}
                              path={`/coaching/${path}/:sessionId`}
                              element={<ProtectedCoachingRoute><Component /></ProtectedCoachingRoute>}
                            />
                          ))}
                          <Route path="/authority-reports" element={<LazyAuthorityReports />} />
                          <Route path="/report/:shareCode" element={<LazyPublicAuthorityReport />} />
                          <Route path="/audit/:shareCode" element={<LazyPublicCompanyBrief />} />
                          <Route path="/brief/:shareCode" element={<CompanyBriefLegacyRedirect />} />
                          <Route path="/scan" element={<LazyAuthorityScan />} />
                          <Route path="/try" element={<ExternalRedirect to="https://nynch.com/next-clients" />} />
                          <Route path="/go" element={<LazyTrackingGoRedirect />} />
                          <Route path="/go/:token" element={<LazyTrackingGoRedirect />} />

                          <Route path="*" element={<LazyNotFound />} />
                        </Routes>
                    </AppAuthBoundary>
                  </BrowserRouter>
              </ModalsProvider>
            </TooltipProvider>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </QueryClientProvider>
);

export default App;
